import SaveIcon from '@mui/icons-material/Save';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { UpdatePieces, useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { AssignableRecipient, PieceAction } from '@apiContract';
import AssignmentList from '@components/DrawerContent/AssignmentList';
import AssignTO from '@components/DrawerContent/AssignmentList/components/AssignTo';
import { Scrollbar } from '@components/Scrollbar';
import LabelSwitch from 'src/components/Inputs/LabelSwitch';
import { useUserPermissions } from 'src/hooks/useUserPermissions';
import { sxScrollWrapper } from 'src/theme/shared/scrollbar';
import { Roles } from 'src/types/shared';

import { showApiErrorToast } from '@utils/apiErrorToast';

import { ActionComponentProps } from '../../index';
import { CollapsibleOptions } from './CollapsibleOptions';

export const Redirect = ({ mailpieces, actionStarted, onActionFinish, isForce }: ActionComponentProps) => {
  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();
  const { isAllowed } = useUserPermissions();

  const isAdmin = isAllowed([Roles.Admin]);

  const [updatePieces] = useUpdatePiecesMutation();

  const [actionInProgress, setActionInProgress] = useState<boolean>(false);
  const [recipient, setRecipient] = useState<AssignableRecipient | null>(null);
  const [comment, setComment] = useState<string>('');
  const [notifyMailroom, setNotifyMailroom] = useState<boolean>(true);
  const [revokeAccessForPreviousRecipients, setRevokeAccessForPreviousRecipients] = useState<boolean>(false);
  const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(false);

  const pieceQuantity = mailpieces.length;

  const handleChangeComment = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  const reset = () => {
    setRecipient(null);
    setComment('');
    setNotifyMailroom(true);
  };

  useEffect(() => {
    if (actionStarted) {
      reset();
    }
  }, [actionStarted]);

  const handleSubmit = async () => {
    if (!recipient) return;

    try {
      setActionInProgress(true);

      let recipientIds: { recipientId: string | null; recipientGroupId: number | null };

      if (!recipient.group && !!recipient.user) {
        recipientIds = { recipientId: recipient.user?.id, recipientGroupId: null };
      } else if (recipient.groupId && !recipient.userId) {
        recipientIds = { recipientId: null, recipientGroupId: recipient.groupId };
      } else {
        recipientIds = { recipientId: recipient.userId ?? null, recipientGroupId: null };
      }

      const params: UpdatePieces = {
        pieces: mailpieces.map((piece) => ({ id: piece.id, ...recipientIds })),
        action: PieceAction.RequestRedirect,
        isForce,
        ...(isForce && { shouldNotifyMailroom: notifyMailroom }),
      };

      if (isAdmin && isForce) {
        params.revokeAccessForPreviousRecipients = revokeAccessForPreviousRecipients;
      }

      await updatePieces(params).unwrap();

      if (comment.trim().length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom: notifyMailroom,
          pieceIds: mailpieces.map((piece) => piece.id),
        }).unwrap();
      }

      onActionFinish();
    } catch (error) {
      showApiErrorToast(error);
    } finally {
      setActionInProgress(false);
    }
  };

  return (
    <>
      <Dialog
        slotProps={{ paper: { sx: { width: 600 } } }}
        open={actionStarted}
        onClose={onActionFinish}
        sx={{ zIndex: '1200' }}
      >
        <DialogTitle>{pieceQuantity > 1 ? 'Redirect selected items' : 'Redirect item'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {pieceQuantity > 1 ? (
              <>
                To redirect <strong>{pieceQuantity}</strong> items and their contents to someone else, select a
                recipient.
              </>
            ) : (
              'To redirect this item and its contents to someone else, select a recipient.'
            )}
          </Typography>

          <AssignTO
            user={recipient}
            unassignedText="Select recipient"
            style={{ padding: 0, width: '100%', marginBottom: '24px' }}
            onClick={() => setIsOpenDrawer(true)}
          />

          <CollapsibleOptions>
            <TextField
              sx={{ width: '100%', mt: 1, mb: 2 }}
              label="Optional Comment"
              multiline
              rows={3}
              value={comment}
              onChange={handleChangeComment}
            />
            <LabelSwitch
              sx={{ mb: 1.5 }}
              checked={notifyMailroom}
              onChange={() => setNotifyMailroom(!notifyMailroom)}
              label="Notify mailroom users about comment"
            />
            {isForce && (
              <LabelSwitch
                checked={revokeAccessForPreviousRecipients}
                onChange={() => setRevokeAccessForPreviousRecipients(!revokeAccessForPreviousRecipients)}
                label="Revoke access for previous recipients"
              />
            )}
          </CollapsibleOptions>
        </DialogContent>
        <DialogActions>
          <Button onClick={onActionFinish} color="inherit" disabled={actionInProgress}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={actionInProgress}
            startIcon={actionInProgress && <SaveIcon />}
            loadingPosition={actionInProgress ? 'start' : undefined}
            variant="contained"
            color="primary"
            disabled={!recipient}
          >
            Redirect
          </Button>
        </DialogActions>
      </Dialog>
      <Drawer
        open={isOpenDrawer}
        onClose={() => setIsOpenDrawer(false)}
        anchor="right"
        slotProps={{ paper: { sx: { width: 1, maxWidth: 480 } } }}
      >
        <Scrollbar sx={sxScrollWrapper}>
          <AssignmentList
            isRedirect
            title="Recipient"
            file={{
              piece: mailpieces[0],
              pieceDetection: mailpieces[0].recipientAutoDetection,
              error: false,
              loading: false,
            }}
            onClose={() => setIsOpenDrawer(false)}
            ignoreHeaderLoading
            withoutUserCreation
            customSave={(user) => {
              setRecipient(user);
              setIsOpenDrawer(false);
            }}
            filterOwner
            saveButtonName="Apply"
          />
        </Scrollbar>
      </Drawer>
    </>
  );
};
